import { RECAPTCHA, SERVICES, SYSTEM } from '../../../util/constants';
import logger from '../../../util/logging.client';
import { API_VALIDATION_ERROR_MESSAGES } from '../errorMessages';
import { getAccountOtc } from './accountVerification.api';

const { V2, V3 } = RECAPTCHA;
const { EIR } = SYSTEM;
const { DUALPLAY, DUALPLAY_PSTN, SA_PSTN, DUALPLAY_NBI, MOBILE } = SERVICES;
const {
  invalidRecaptcha,
  ivalidAccountData,
  invalidAccount,
  mobileOnlyCustomer,
  genericError,
  accountNotFound,
} = API_VALIDATION_ERROR_MESSAGES;

/**
 * @name getAccountOTCData
 * @description Fetches data from the GRAPHQL API
 * @param {import('@apollo/client').ApolloClient<object>} graphql - Apollo client
 * @param {string} recaptchaV3Token - recaptcha version 3 token
 * @param {string} accountNumber - User input account number
 * @param {number} landLineNumber - User input broadband or landline number
 * @param {string} captchaV2 - recaptcha version 3 token
 * @param {string} setEnableCaptchaV2 - Enables recaptcha version 2 if v3 fails
 * @param {string} setApiError - Set the value fo api error
 * @param {string} setDisableCTA - Enables or disables CTA
 * @param {string} showMobileSupportModal - Toggles mobile support modal
 * @param {string} showAccountVerificationModal - Toggles AccountVerification Modal
 * @param {string} showBroadbandSupportModal - Toggles broadband support modal
 * @param {string} showEirSupportModal - Toggles eir support modal
 * @param {string} setSweeprParams - set the values for sweepr launch
 * @returns {Promise<void>} Promise and sets the state clientSideGRAPHQL when resolved
 */
export const getAccountOTCInfo = async (
  graphql,
  recaptchaV3Token,
  accountNumber,
  landLineNumber,
  captchaV2,
  setEnableCaptchaV2,
  setApiError,
  setDisableCTA,
  showMobileSupportModal,
  showAccountVerificationModal,
  showBroadbandSupportModal,
  showEirSupportModal,
  setSweeprParams,
) => {
  try {
    const variables = {
      accountId: accountNumber,
      systemId: EIR,
      telNo: landLineNumber,
      recaptcha: captchaV2?.account || recaptchaV3Token,
      recaptchaVersion: captchaV2?.account ? V2 : V3,
    };
    setSweeprParams(prev => ({ ...prev, OTC: null }));
    const data = await getAccountOtc(graphql, variables);
    if (data) {
      setApiError(prev => ({
        ...prev,
        invalidLogin: false,
        invalidAccount: false,
        errorMessage: null,
      }));
    }
    if (data?.accountOtc?.isR6) {
      showEirSupportModal.enable();
      showAccountVerificationModal.disable();
    } else if (data?.accountOtc?.isION) {
      if (
        data?.accountOtc?.ionServices?.includes(DUALPLAY) ||
        data?.accountOtc?.ionServices?.includes(DUALPLAY_PSTN) ||
        data?.accountOtc?.ionServices?.includes(SA_PSTN)
      ) {
        setSweeprParams(prev => ({ ...prev, OTC: data?.accountOtc?.onetimecode }));
        // showAccountVerificationModal.disable();
      } else if (data?.accountOtc?.ionServices?.includes(DUALPLAY_NBI)) {
        showBroadbandSupportModal.enable();
        showAccountVerificationModal.disable();
      } else if (data?.accountOtc?.ionServices?.includes(MOBILE)) {
        showMobileSupportModal.enable();
        showAccountVerificationModal.disable();
      }
    }
    return data;
  } catch (error) {
    if (error?.message?.includes(invalidRecaptcha)) {
      setEnableCaptchaV2({ login: false, account: true });
      setDisableCTA(true);
    } else if (error?.message?.includes(mobileOnlyCustomer)) {
      showMobileSupportModal.enable();
      showAccountVerificationModal.disable();
    } else if (
      error?.message?.includes(ivalidAccountData) ||
      error?.message?.includes(accountNotFound)
    ) {
      setApiError(prev => ({
        ...prev,
        invalidLogin: false,
        invalidAccount: true,
        errorMessage: invalidAccount,
      }));
      setDisableCTA(true);
    } else {
      setApiError(prev => ({
        ...prev,
        invalidLogin: false,
        invalidAccount: true,
        errorMessage: genericError,
      }));
      setDisableCTA(true);
    }
    logger.log('error', 'Error executing accountOtc:', error);
    return error;
  }
};
